import type { Opaque } from "type-fest";
import cookie from "cookie";

const CACHE_KEY = "token";
const OKTA_CALLBACK_MESSAGE = "EVENT:OKTA_CALLBACK" as const;
const OKTA_CALLBACK_ERROR_MESSAGE = "EVENT:OKTA_CALLBACK_ERROR" as const;

type HybridToken = Opaque<string>;
type TokenState = {
  token: HybridToken;
  tokenType: TokenType;
  source: "one" | "splat";
  /**
   * Unix Timestamp
   * @example 1674748642
   */
  expiration?: number;
};

const clearJwtCache = () => {
  localStorage.removeItem(CACHE_KEY);
  document.cookie = "token=; Max-Age=-99999999;";
};

const getJwtCache = (): TokenState | undefined => {
  try {
    try {
      const tokenFromStorage = localStorage.getItem(CACHE_KEY);
      const localStorageToken = tokenFromStorage
        ? JSON.parse(tokenFromStorage)
        : undefined;
      if (
        localStorageToken &&
        localStorageToken.token &&
        localStorageToken.tokenType
      ) {
        return localStorageToken;
      }
    } catch (e) {}
    const c = cookie.parse(document.cookie);
    const cookieToken = c?.token;
    if (cookieToken) {
      const cookieTokenState = JSON.parse(cookieToken) as TokenState;
      if (
        cookieTokenState &&
        cookieTokenState.token &&
        cookieTokenState.tokenType
      ) {
        return cookieTokenState;
      }
    }
  } catch (e) {}
  return;
};

const setJwtCache = (state: TokenState): void => {
  localStorage.setItem(CACHE_KEY, JSON.stringify(state));
  document.cookie = cookie.serialize(CACHE_KEY, JSON.stringify(state), {
    maxAge: 60 * 60 * 18, // 18 hours
  });
};

const handleTokenStorageEvent = (): Promise<TokenState> => {
  return new Promise((resolve) => {
    const handler = (event: StorageEvent) => {
      if (event.key === CACHE_KEY && event.newValue) {
        resolve(JSON.parse(event.newValue));
        window.removeEventListener("storage", handler);
      }
    };
    window.addEventListener("storage", handler);
  });
};

const postOktaCallbackToOpener = (tokenState: TokenState): void => {
  if (!window.opener) return;
  window.opener.postMessage(
    { type: OKTA_CALLBACK_MESSAGE, payload: tokenState },
    window.location.origin
  );
};

const postOktaCallbackErrorToOpener = (message: string): void => {
  if (!window.opener) return;
  window.opener.postMessage(
    { type: OKTA_CALLBACK_ERROR_MESSAGE, payload: { message } },
    window.location.origin
  );
};

const handleOktaAuthCallback = (): Promise<TokenState> => {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener("message", messageHandler);
      window.removeEventListener("storage", storageHandler);
    };

    const messageHandler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === OKTA_CALLBACK_MESSAGE) {
        cleanup();
        resolve(event.data.payload);
      } else if (event.data?.type === OKTA_CALLBACK_ERROR_MESSAGE) {
        cleanup();
        reject(new Error(event.data.payload.message));
      }
    };

    const storageHandler = (event: StorageEvent) => {
      if (event.key === CACHE_KEY && event.newValue) {
        cleanup();
        resolve(JSON.parse(event.newValue));
      }
    };

    window.addEventListener("message", messageHandler);
    window.addEventListener("storage", storageHandler);
  });
};

export type { TokenState, HybridToken };
export {
  clearJwtCache,
  getJwtCache,
  handleOktaAuthCallback,
  handleTokenStorageEvent,
  postOktaCallbackErrorToOpener,
  postOktaCallbackToOpener,
  setJwtCache,
};
